import { COMPONENT_STATES } from 'common/constants';
import { getComputedValue } from 'common/utils/get-computed-component-props';
import { getMediaKeysInOrderOfInheritance } from 'common/utils/get-media-keys-in-order-of-inheritance';
import { orderMediaQueryConfig } from 'common/utils/story-media-query';
import type { BBStates, StoryMediaQuery } from 'types/story';
import type { DesignDefinition, DesignModeSelection } from './types';
import {
	collectEffectiveStyleAssignments,
	type StyleAssignmentLayer,
	type DesignPresetStyles,
	type EffectiveStyleAssignments,
} from './preset-styles';
import { getDesignPresetTokenId } from './presets';
import { resolveDesignModeValue } from './modes';

export type PresetFontFace = { family: string; weight?: number };
export function hasPresetTypography(assignments: EffectiveStyleAssignments): boolean {
	return Object.values(assignments.sources).some(platforms =>
		Object.values(platforms).some(
			sources => sources.fontFamily?.kind === 'preset' || sources.fontWeight?.kind === 'preset'
		)
	);
}

export function collectEffectiveFontFaces({
	design,
	mediaQuery,
	styles,
}: {
	design?: DesignDefinition;
	mediaQuery: StoryMediaQuery;
	styles: DesignPresetStyles;
}): PresetFontFace[] {
	const tokens = new Map(
		design?.tokens.collections.flatMap(collection => collection.tokens.map(token => [token.id, token] as const))
	);
	const selections = (design?.modeGroups ?? []).reduce<DesignModeSelection[]>(
		(previous, group) =>
			previous.flatMap(selection => group.modes.map(mode => ({ ...selection, [group.id]: mode.id }))),
		[{}]
	);
	const resolve = (value: unknown, selection: DesignModeSelection): unknown => {
		const token = tokens.get(getDesignPresetTokenId(value) ?? '');
		return token ? resolveDesignModeValue<string | number>(token.value, selection) : value;
	};
	const faces = new Map<string, PresetFontFace>();
	const ordered = orderMediaQueryConfig(mediaQuery, { defaultFirst: true, minOrder: 'asc', maxOrder: 'desc' });
	for (const platform of Object.keys(mediaQuery.config)) {
		const keys = getMediaKeysInOrderOfInheritance(mediaQuery.defaultPlatform, mediaQuery.config[platform], ordered);
		for (const state of Object.values(COMPONENT_STATES)) {
			const inherited = getComputedValue({
				source: styles,
				sourceKey: 'styles',
				mediaKeysInOrderOfInheritance: keys,
				currState: state as BBStates,
			}).value as Record<string, unknown>;
			for (const selection of selections) {
				const family =
					resolve(inherited.fontFamily, selection) ??
					(design && resolveDesignModeValue(design.defaults.root.fontFamily, selection));
				if (typeof family !== 'string' || !family || getDesignPresetTokenId(family)) continue;
				const weight = resolve(inherited.fontWeight, selection);
				const face = {
					family,
					...(weight !== undefined
						? { weight: Number(weight === 'normal' ? 400 : weight === 'bold' ? 700 : weight) }
						: {}),
				};
				faces.set(`${face.family}:${face.weight ?? ''}`, face);
			}
		}
	}
	return [...faces.values()];
}

export function collectDesignPresetRequiredFontFaces({
	design,
	presetId,
	mediaQuery,
	layers = [],
}: {
	design: DesignDefinition;
	presetId: string;
	mediaQuery: StoryMediaQuery;
	layers?: readonly StyleAssignmentLayer[];
}): PresetFontFace[] {
	const preset = design.presets?.find(item => item.id === presetId);
	if (!preset || (!preset.properties.fontFamily && !preset.properties.fontWeight)) return [];
	const collected = collectEffectiveStyleAssignments({ design, presetId, mediaQuery, layers });
	return collectEffectiveFontFaces({ design, mediaQuery, styles: collected.styles });
}
